import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ButtonGroup,
  Box,
  Grid,
  Heading,
  Checkbox,
  useColorModeValue,
  FormLabel,
  FormControl,
  Stat,
  useTheme,
} from "@chakra-ui/react";
import { MapRef, MapLayerMouseEvent } from "react-map-gl";
import { BBox } from "geojson";

import { DatePicker, InferencesLayer, Overline, Map, MapGrid, MapSpeciesLegend, MapScenesLegend, StatsCard, StatsLabel, StatsNumber, Loading, LoadingStatsCard } from "../../components";
import { ViewModes } from "../Scenes/types";

import { useAuth } from "../../context";
import { MIN_DATE,MAX_DATE } from "../../settings";
import { InferencePopup } from "./InferencePopup";
import { Inference } from "../../types/inferences";
import { getLikelySpecies, getSortedSpecies } from "../../utils";
import { addSpeciesCount, InferenceCounts } from "../../utils/inferenceStats";
import { useInferences, useQueryParams, useScenes } from "../../hooks";
import { PANEL_SIZE } from "../../theme";

function Explore() {
  const mapRef = useRef<MapRef>(null);
  const { isAuthenticated } = useAuth();
  const [cursor, setCursor] = useState<string>("auto");
  const [filterBbox, setFilterBbox] = useState<boolean>(false);
  const [dataViewMode, setDataViewMode] = useState<ViewModes>(
    isAuthenticated ? ViewModes.User : ViewModes.Public
  );
  const [showOnlyValidated, setShowOnlyValidated] = useState<boolean>(isAuthenticated ? false : true);
  const [selectedInference, setSelectedInference] = useState<Inference>();
  const theme = useTheme();
  const {
    selectedDates,
    setSelectedDates,
    bbox,
    setBbox,
  } = useQueryParams();
  const { scenes } = useScenes({
    selectedDates,
    bbox,
    subResource: dataViewMode === ViewModes.User ? "user" : ""
  });
  const { inferences, isLoading: inferencesLoading } = useInferences({
    selectedDates,
    bbox,
    validated: showOnlyValidated ? true : undefined,
    asPoints: dataViewMode !== ViewModes.User,
    subResource: dataViewMode === ViewModes.User ? "user" : ""
  });

  const { validated, species }: InferenceCounts = useMemo(() => {
    if (!inferences?.features) return {
      count: 0,
      validated: 0,
      species: {}
    };

    return inferences.features.reduce((summaryObject, inference) => {
      const { species } = getLikelySpecies(inference);
      const speciesCount = addSpeciesCount(summaryObject.species, { [species]: 1 });
      const validated = inference.properties.is_valid !== null ? summaryObject.validated + 1 : summaryObject.validated;

      return {
        ...summaryObject,
        validated,
        species: speciesCount
      };
    }, {
      count: inferences.features.length,
      validated: 0,
      species: {}
    });
  }, [inferences]);
  const topSpecies = useMemo(() => {
    return getSortedSpecies(species);
  }, [species]);
  const checkboxBg = useColorModeValue("base.50", "base.500");
  const borderColor = useColorModeValue("base.100a", "base.500");

  useEffect(() => {
    if (filterBbox) {
      setBbox(mapRef.current?.getBounds().toArray().flat() as BBox);
    } else {
      setBbox(undefined);
    }
  }, [filterBbox, setBbox]);

  const handleViewChange = () => {
    setDataViewMode(dataViewMode === ViewModes.User ? ViewModes.Public : ViewModes.User);
    setSelectedInference(undefined);
  };

  const handleBboxChange = useCallback(() => {
    if (filterBbox) {
      setBbox(mapRef.current?.getBounds().toArray().flat() as BBox);
    }
  }, [filterBbox, setBbox]);

  const onMouseEnter = useCallback(() => setCursor("pointer"), []);
  const onMouseLeave = useCallback(() => setCursor("auto"), []);

  const onClick = (event: MapLayerMouseEvent) => {
    if (event.features?.length) {
      const selected = event.features[0];
      const feature = inferences?.features.find(
        (f) => selected.properties?.id === f.properties?.id
      );
      if (feature) {
        setSelectedInference(feature as Inference);
      } else {
        setSelectedInference(undefined);
      }
    }
  };

  return (
    <Box
      position="relative"
      display="grid"
      gridTemplateColumns={["1fr", null, `${PANEL_SIZE} 1fr`]}
      gridTemplateRows={["3fr 1fr", null, "1fr"]}
      overflow="hidden"
      mx={0}
    >
      <Grid
        templateRows="max-content max-content max-content 1fr"
        p="5"
        gap="5"
        overflow="auto"
        boxShadow={[
          "0 -4px 6px -1px rgba(0, 0, 0, 0.1), 0 -2px 4px -1px rgba(0, 0, 0, 0.06)",
          null,
          "md",
        ]}
        gridRow={["2", null, "initial"]}
        zIndex="10"
        borderRight="1px solid"
        borderColor={borderColor}
      >
        <Box display="flex" flexDir="column" gap="5">
          <Heading size="md" as="h2" flex="1">
            Species Detected
          </Heading>
          <StatsCard>
            {inferencesLoading ? (
              Array.from(Array(3)).map((i, ind) => (
                // eslint-disable-next-line react/no-array-index-key
                <LoadingStatsCard key={ind} />))
            ) : topSpecies.length > 0 ? (
              <>
                {topSpecies.map(([label, value]) => (
                  <Stat key={label} textAlign="right" flex="initial">
                    <StatsNumber>{value}</StatsNumber>
                    <StatsLabel>{label}</StatsLabel>
                  </Stat>
                ))}
                <Stat textAlign="right" flex="initial">
                  <StatsNumber>{validated}</StatsNumber>
                  <StatsLabel>Validated</StatsLabel>
                </Stat>
              </>
            ) : (
              <Overline>No species detected</Overline>
            )}
          </StatsCard>
          {isAuthenticated && (
            <FormControl pt="5">
              <Box display="flex" flexDir="column">
                <FormLabel htmlFor="editMode" display="inline">
                  Show detections from:
                </FormLabel>
                {isAuthenticated && (
                  <>
                    <ButtonGroup isAttached id="editMode" display="flex">
                      <Button
                        size="lg"
                        variant={
                          dataViewMode === ViewModes.User
                            ? "primary"
                            : "primary-outline"
                        }
                        onClick={handleViewChange}
                        flex="1"
                      >
                        My Scenes
                      </Button>
                      <Button
                        size="lg"
                        variant={
                          dataViewMode === ViewModes.Public
                            ? "primary"
                            : "primary-outline"
                        }
                        onClick={handleViewChange}
                        flex="1"
                      >
                        All Users&apos; Scenes
                      </Button>
                    </ButtonGroup>
                    <Box display="flex" flexDir="column" pt="5">
                      <Checkbox
                        mb="1"
                        id="showAllValidated"
                        isChecked={showOnlyValidated}
                        onChange={() => setShowOnlyValidated((prev) => !prev)}
                        colorScheme="primary"
                      >
                        Show validated detections only
                      </Checkbox>
                    </Box>
                  </>
                )}
              </Box>
            </FormControl>
          )}
        </Box>
        <MapSpeciesLegend />
      </Grid>
      <Box display="grid" gridTemplateRows="1fr max-content">
        <Box position="relative">
          <Box
            position="absolute"
            top="4"
            left="4"
            zIndex="1000"
            borderRadius="md"
            padding="2"
            boxShadow="base"
            bgColor={checkboxBg}
            border="1px solid"
            borderColor="base.100a"
          >
            <Checkbox
              colorScheme="primary"
              checked={filterBbox}
              onChange={() => setFilterBbox((prev) => !prev)}
            >
              Filter detections by the map
            </Checkbox>
          </Box>
          <Map
            ref={mapRef}
            onDragEnd={handleBboxChange}
            onZoomEnd={handleBboxChange}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            cursor={cursor}
            interactiveLayerIds={["inferences"]}
            onClick={onClick}
          >
            {inferencesLoading && (
              <Loading
                fontSize="sm"
                fontFamily={theme.fonts.body}
                position="absolute"
                top={6}
                right={6}
                display="flex"
                alignItems="center"
                gap={4}
                fontWeight="semibold"
              >
                Loading detections
              </Loading>
            )}
            {inferences && (
              <InferencesLayer
                data={inferences}
                id="inferences"
              />
            )}
            <MapGrid data={scenes} beforeId="inferences" />
            {selectedInference && (
              <InferencePopup
                inference={selectedInference}
                onClose={() => setSelectedInference(undefined)}
                includeThumbnail={dataViewMode === ViewModes.User}
              />
            )}
            <MapScenesLegend />
          </Map>
        </Box>
        <Box py="8" px="8" pt="4">
          <DatePicker
            minDate={MIN_DATE}
            maxDate={MAX_DATE}
            selectedDates={selectedDates}
            setSelectedDates={setSelectedDates}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default Explore;
